import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserData } from '../auth/UserData';
import { createCheckoutSession, createPortalSession, upgradeMembership } from '../services/membershipClient';
import SEO from '../components/SEO';
import MarketingPage from './landing/MarketingPage';
import { useTheme } from '../context/ThemeContext';
import { PLANS as CANONICAL_PLANS } from '../config/plans';
import './landing/landing.css';

const ACTIVE_STATUSES = new Set(['active', 'trialing']);
const MANAGEABLE_STATUSES = new Set(['active', 'trialing', 'past_due']);

// Only the current landing-aligned plans are shown for purchase. Legacy
// basic/pro/elite are hidden here but kept in DEFAULT_PLANS below so existing
// subscribers still see their plan name.
const PLAN_ORDER = ['essentials', 'professional'];

const PLAN_STYLES = {
  essentials: {
    accent: '#2b7bff',
    buttonBg: '#2b7bff',
    buttonHover: '#1d5dd1',
  },
  professional: {
    accent: '#fcd34d',
    buttonBg: '#fcd34d',
    buttonHover: '#fbbf24',
  },
  basic: {
    accent: '#2b7bff',
    buttonBg: '#2b7bff',
    buttonHover: '#1d5dd1',
  },
  pro: {
    accent: '#fcd34d',
    buttonBg: '#fcd34d',
    buttonHover: '#fbbf24',
  },
  elite: {
    accent: '#f0625f',
    buttonBg: '#f0625f',
    buttonHover: '#d94440',
  },
};

// Build a /pricing plan entry from the canonical landing config so the app and
// the landing page can never show different names/prices/features.
const fromCanonical = (key) => {
  const p = CANONICAL_PLANS[key];
  return {
    name: p.name,
    tagline: p.tagline,
    priceAmount: `$${p.price}`,
    pricePeriod: '/ month',
    cta: `Choose ${p.name}`,
    ...(p.highlightLabel ? { highlightLabel: p.highlightLabel } : {}),
    features: p.features,
  };
};

const DEFAULT_PLANS = {
  // Current plans — single source of truth is src/config/plans.js.
  essentials: fromCanonical('essentials'),
  professional: fromCanonical('professional'),
  // Legacy plans — hidden from /pricing (see PLAN_ORDER), retained only so an
  // existing basic/pro/elite subscriber still sees their plan name.
  basic: {
    name: 'BASIC',
    tagline: 'Sentiment and NewsDesk together in one feed.',
    oldPrice: '$29.99',
    priceAmount: '$14.99',
    pricePeriod: '/ month',
    cta: 'Choose Basic',
    discountLabel: 'SAVE 50%',
    features: [
      'Sentiment analysis from multiple brokers gathered in one place',
      'Live NewsDesk with real-time headlines (worth $50 on its own)',
      'Ask questions inside the Trading Floor and get answers',
      'Glossary with terms, illustrations, and explanations',
      'Week Ahead – early insight into upcoming earnings and market direction',
    ],
  },
  pro: {
    name: 'PRO',
    highlightLabel: 'MOST POPULAR',
    tagline: 'Orderflow insights, live calls, and mentor feedback.',
    oldPrice: '$59.99',
    priceAmount: '$29.99',
    pricePeriod: '/ month',
    cta: 'Choose Pro',
    discountLabel: 'SAVE 50%',
    features: [
      'Everything in Basic +',
      'Flow Calls 2–3 times per week – live orderflow and technical analysis sessions',
      'Option Flow Dashboard – real-time institutional orderflow for stocks & ETFs',
      'Flow Breakdown – aggregated flow analysis by ticker, sector, and expiry',
      'Flow Deep Dive – drill into individual trades, block orders, and sweeps',
      'Full access to market intelligence – use flow data as added confirmation for your trades',
      'Analysis & feedback channel – mentors review your setups and give direct feedback',
      'Trade Journal – log trades, tag setups, and track your performance over time',
      'Academy – courses, quizzes, chart studies, and structured learning paths',
    ],
  },
  elite: {
    name: 'ELITE',
    highlightLabel: '🏆 For dedicated traders',
    tagline: 'Priority support with a 1:1 session included.',
    oldPrice: '$179.99',
    priceAmount: '$89.99',
    pricePeriod: '/ month',
    cta: 'Choose Elite',
    discountLabel: 'SAVE 50%',
    features: [
      'Everything in Pro +',
      'Priority support and analysis help – in group and direct message',
      '1:1 Coaching session included (1 hour each month – $99 value)',
      'Early access to new features and tools before public release',
    ],
  },
};

const normalizePlanKey = (planNickname) => {
  if (!planNickname) return null;
  const normalized = String(planNickname).trim().toLowerCase();
  if (normalized.includes('elite')) return 'elite';
  if (normalized.includes('pro')) return 'pro';
  if (normalized.includes('basic')) return 'basic';
  return normalized || null;
};

const formatPrice = (price, currency = 'usd') => {
  const amount = price;
  const symbols = { usd: '$', eur: '€', gbp: '£', nok: 'kr ', sek: 'kr ', dkk: 'kr ' };
  const suffix = ['nok', 'sek', 'dkk'].includes(currency) ? '' : '';
  const prefix = symbols[currency] || `${currency.toUpperCase()} `;
  if (['nok', 'sek', 'dkk'].includes(currency)) {
    return `${amount.toLocaleString('nb-NO')} ${currency.toUpperCase()}`;
  }
  return `${prefix}${amount.toFixed(2)}`;
};

const intervalLabels = { month: '/ month', year: '/ year', week: '/ week' };

const sanitizeHtml = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll('script,iframe,form,object,embed').forEach(el => el.remove());
  div.querySelectorAll('*').forEach(el => {
    for (const attr of [...el.attributes]) {
      if (attr.name.startsWith('on')) el.removeAttribute(attr.name);
    }
  });
  return div.innerHTML;
};

const Membership = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading, refreshUserData } = useUserData();
  const { tenantConfig } = useTheme();

  const [tenantPlans, setTenantPlans] = useState(null);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [portalPending, setPortalPending] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [pageSettings, setPageSettings] = useState({});

  // Plans are managed in tenants.plans (admin → Membership Plans) for every
  // tenant including FlockTrade, so always fetch them. Falls back to
  // DEFAULT_PLANS only when none are configured.
  useEffect(() => {
    fetch('/api/tenant/plans')
      .then((r) => r.ok ? r.json() : [])
      .then((data) => { if (Array.isArray(data) && data.length > 0) setTenantPlans(data); })
      .catch(() => {});
  }, [tenantConfig]);

  // Fetch membership page settings
  useEffect(() => {
    fetch('/api/tenant/membership-page-settings')
      .then((r) => r.ok ? r.json() : {})
      .then((data) => { if (data && typeof data === 'object') setPageSettings(data); })
      .catch(() => {});
  }, []);

  const hasSyncedRef = useRef(false);
  const countdownTargetRef = useRef(Date.now() + 13 * 60 * 60 * 1000);
  const [countdown, setCountdown] = useState('');

  const membership = user?.membership || { status: 'inactive' };
  const membershipStatus = membership.status || 'inactive';
  const isActive = ACTIVE_STATUSES.has(membershipStatus);
  const canManageMembership = MANAGEABLE_STATUSES.has(membershipStatus);
  const isPastDue = membershipStatus === 'past_due';
  const membershipChipColor = isActive ? 'success' : isPastDue ? 'warning' : 'default';
  const isDiscordLinked = Boolean(user?.discordLinked);
  const membershipPlanKey =
    typeof membership.planKey === 'string' && membership.planKey
      ? membership.planKey
      : normalizePlanKey(membership.planNickname);
  const currentPlanDisplayName =
    membership.planDisplayName
    || (membershipPlanKey ? DEFAULT_PLANS[membershipPlanKey]?.name : null)
    || (typeof membership.planNickname === 'string' ? membership.planNickname : null);

  const membershipStatusLabel = useMemo(
    () => t(`membership.status.${membershipStatus}`, { defaultValue: membershipStatus }),
    [membershipStatus, t]
  );

  const isCustomTenant = tenantConfig && tenantConfig.slug !== 'flocktrade';

  const plans = useMemo(() => {
    // If tenant has custom plans, use those
    if (tenantPlans && tenantPlans.length > 0) {
      return tenantPlans.map((tp, idx) => {
        const planStyles = PLAN_STYLES[tp.key] || Object.values(PLAN_STYLES)[idx % 3] || PLAN_STYLES.basic;
        return {
          key: tp.key,
          name: tp.name,
          tagline: tp.tagline || '',
          highlightLabel: tp.highlightLabel || '',
          oldPrice: tp.oldPrice || '',
          priceAmount: formatPrice(tp.price, tp.currency),
          pricePeriod: intervalLabels[tp.interval] || '/ month',
          discountLabel: tp.discountLabel || '',
          cta: t('membership.actions.subscribe', { defaultValue: `Choose ${tp.name}` }),
          features: tp.features || [],
          styles: planStyles,
        };
      });
    }

    // Non-FlockTrade tenant with no plans configured — return empty
    if (isCustomTenant) return [];

    // Default: use translation-based plans (FlockTrade)
    const rawPlans = t('membership.plans', {
      returnObjects: true,
      defaultValue: DEFAULT_PLANS,
    });

    const planSource = rawPlans && typeof rawPlans === 'object' && !Array.isArray(rawPlans)
      ? rawPlans
      : DEFAULT_PLANS;

    return PLAN_ORDER.map((key) => {
      const plan = planSource[key] || DEFAULT_PLANS[key];
      if (!plan) return null;

      const planStyles = PLAN_STYLES[key] || PLAN_STYLES.basic;
      const features = Array.isArray(plan.features) ? plan.features : DEFAULT_PLANS[key].features;

      return {
        key,
        ...plan,
        styles: planStyles,
        features,
        priceAmount: plan.priceAmount || plan.price || DEFAULT_PLANS[key].priceAmount,
        pricePeriod: plan.pricePeriod || DEFAULT_PLANS[key].pricePeriod,
        oldPrice: plan.oldPrice || DEFAULT_PLANS[key].oldPrice,
        discountLabel: plan.discountLabel || DEFAULT_PLANS[key].discountLabel,
        cta: plan.cta || t('membership.actions.subscribe'),
      };
    }).filter(Boolean);
  }, [t, tenantPlans]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const state = params.get('state');

    if (!state) return;

    if (state === 'success') {
      setAlert({ severity: 'success', message: t('membership.alerts.success') });
      if (!hasSyncedRef.current) {
        hasSyncedRef.current = true;
        (async () => {
          try {
            setStatusLoading(true);
            await refreshUserData();
          } catch (error) {
            console.error('Failed to refresh membership after checkout:', error.message);
          } finally {
            setStatusLoading(false);
          }
        })();
      }
    } else if (state === 'cancel') {
      setAlert({ severity: 'info', message: t('membership.alerts.cancelled') });
    }
  }, [location.search, refreshUserData, t]);

  useEffect(() => {
    const hourSymbol = i18n.language?.startsWith('no') ? 't' : 'h';
    const minuteSymbol = 'm';
    const secondSymbol = 's';

    const updateCountdown = () => {
      const diff = Math.max(0, countdownTargetRef.current - Date.now());
      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setCountdown(
        `${String(hours).padStart(2, '0')}${hourSymbol} ${String(minutes).padStart(2, '0')}${minuteSymbol} ${String(seconds).padStart(2, '0')}${secondSymbol}`
      );
    };

    updateCountdown();
    const intervalId = setInterval(updateCountdown, 1000);

    return () => clearInterval(intervalId);
  }, [i18n.language]);

  const ensureAuthenticated = () => {
    if (user) return true;
    navigate('/login', { replace: true });
    return false;
  };

  const handleSubscribe = async (planKey) => {
    if (!ensureAuthenticated()) return;

    try {
      setPendingPlan(planKey);
      setAlert(null);
      const { url } = await createCheckoutSession(planKey);
      if (url) {
        window.location.href = url;
      } else {
        setAlert({ severity: 'error', message: t('membership.alerts.genericError') });
      }
    } catch (error) {
      console.error('Checkout initiation failed:', error.message);
      setAlert({ severity: 'error', message: t('membership.alerts.genericError') });
    } finally {
      setPendingPlan(null);
    }
  };

  // /app/pricing?plan=<key>&start=1 — arrived from the signup trial intent
  // (see utils/pendingPlan.js). Go straight to Stripe checkout once; the
  // `start` flag is stripped first so browser-back from Stripe doesn't
  // re-trigger it.
  const autoStartRef = useRef(false);
  useEffect(() => {
    if (autoStartRef.current || isLoading || !user) return;
    const params = new URLSearchParams(location.search);
    const planParam = params.get('plan');
    if (!planParam || params.get('start') !== '1') return;
    if (ACTIVE_STATUSES.has(membershipStatus)) return;
    if (!plans.some((p) => p.key === planParam)) return;
    autoStartRef.current = true;
    params.delete('start');
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
    handleSubscribe(planParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, user, location.search, membershipStatus, plans]);

  const handleUpgrade = async (planKey, planName) => {
    if (!ensureAuthenticated()) return;

    try {
      setPendingPlan(planKey);
      setAlert(null);
      await upgradeMembership(planKey);
      setAlert({ severity: 'success', message: t('membership.alerts.upgradeSuccess', { planName }) });

      setStatusLoading(true);
      try {
        await refreshUserData();
      } catch (refreshError) {
        console.error('Failed to refresh membership after upgrade:', refreshError.message);
      } finally {
        setStatusLoading(false);
      }
    } catch (error) {
      const responseMessage = error?.response?.data?.error;
      console.error('Membership upgrade failed:', responseMessage || error.message);
      setAlert({
        severity: 'error',
        message: responseMessage || t('membership.alerts.upgradeError'),
      });
    } finally {
      setPendingPlan(null);
    }
  };

  const handlePortal = async () => {
    if (!ensureAuthenticated()) return;

    try {
      setPortalPending(true);
      setAlert(null);
      const { url } = await createPortalSession();
      if (url) {
        window.location.href = url;
      } else {
        setAlert({ severity: 'error', message: t('membership.alerts.genericError') });
      }
    } catch (error) {
      console.error('Portal session failed:', error.message);
      setAlert({ severity: 'error', message: t('membership.alerts.portalError') });
    } finally {
      setPortalPending(false);
    }
  };

  if (isLoading) {
    return (
      <MarketingPage wide>
        <div className="auth-loading"><span className="spinner" style={{ color: 'var(--orange)' }} /></div>
      </MarketingPage>
    );
  }

  return (
    // MarketingPage supplies the v2 nav, footer and page padding, the same
    // chrome as /about, /terms and the rest of the marketing surface. This page
    // used to sit inside AppLayout, which meant new pricing cards framed by the
    // old MUI header and footer. `wide` gives the 1100px column the plan grid
    // needs; the old `mp-page`/`mp-inner` wrappers duplicated that and are gone.
    <MarketingPage wide>
      <SEO
        title="Pricing — Plans From $29/mo"
        description="Choose Essentials or Professional. Get 90+ panels, options flow, sentiment, geopolitical intelligence and an AI research assistant. 7-day free trial, cancel anytime."
        path="/pricing"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'FlockTrade Membership',
          description: 'Market intelligence terminal membership plans for serious self-directed traders.',
          brand: { '@type': 'Brand', name: 'FlockTrade' },
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'USD',
            lowPrice: '29',
            highPrice: '49',
            offerCount: 2,
          },
        }}
      />

      <div className="mp-head">
          {pageSettings.show_promo_banner && pageSettings.promo_banner_text && (
            <div className="mp-promo">{pageSettings.promo_banner_text}</div>
          )}

          <div className="section-label">Pricing</div>
          <h1 className="page-title">{pageSettings.headline || 'Flexible, transparent pricing'}</h1>
          <p className="page-lead">{pageSettings.subheadline || 'Cancel any time – keep your launch price forever.'}</p>

          {pageSettings.show_lock_in_message !== false && (
            <div className="mp-lockin">{pageSettings.lock_in_text || 'Lock in the price you buy at – it increases gradually as more members join.'}</div>
          )}

          {pageSettings.show_launch_badge !== false && (
            <div className="mp-launch">{pageSettings.launch_badge_text || 'Launch pricing — lock in your rate today'}</div>
          )}

          {pageSettings.show_discount_code && pageSettings.discount_code && (
            <div className="mp-promoline green">Everyone gets {pageSettings.discount_percentage}% off &ndash; use code: <strong>{pageSettings.discount_code}</strong></div>
          )}
          {pageSettings.show_partner_discount && pageSettings.partner_name && (
            <div className="mp-promoline cyan">Become {pageSettings.partner_name} member and get {pageSettings.partner_discount_percentage}% off</div>
          )}
        </div>

        <div className="mp-status">
          <span className={`mp-pill ${membershipChipColor === 'success' ? 'success' : membershipChipColor === 'warning' ? 'warning' : ''}`}>
            {statusLoading ? t('membership.status.refreshing') : membershipStatusLabel}
          </span>

          {user && (
            <span className="mp-pill">
              {currentPlanDisplayName
                ? `${t('membership.planSummary.label')}: ${currentPlanDisplayName}`
                : t('membership.planSummary.none')}
            </span>
          )}

          {canManageMembership && (
            <button type="button" className="btn btn-ghost" onClick={handlePortal} disabled={portalPending || statusLoading}>
              {portalPending ? <span className="spinner" /> : t('membership.actions.manage')}
            </button>
          )}

          {!isDiscordLinked && (
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/app/account')}>
              {t('membership.actions.linkDiscord')}
            </button>
          )}
        </div>

        {membership.cancelAtPeriodEnd && membership.currentPeriodEnd && (
          <div className="mp-note">
            {t('membership.status.cancelledAt', { date: new Date(membership.currentPeriodEnd).toLocaleString() })}
          </div>
        )}

        {membership.currentPeriodEnd && isActive && !membership.cancelAtPeriodEnd && (
          <div className="mp-note">
            {t('membership.status.renews', { date: new Date(membership.currentPeriodEnd).toLocaleString() })}
          </div>
        )}

        {alert && (
          <div className={`auth-alert mp-alert ${alert.severity === 'success' ? 'success' : alert.severity === 'info' ? 'info' : 'error'}`}>
            {alert.message}
          </div>
        )}

        {!user && (
          <div className="auth-alert mp-alert info">{t('membership.alerts.loginRequired')}</div>
        )}

        {pageSettings.custom_html_above_plans && (
          <div className="mp-custom-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(pageSettings.custom_html_above_plans) }} />
        )}

        {isCustomTenant && plans.length === 0 && (
          <div className="mp-empty">
            <h3>No membership plans available yet</h3>
            <p>This community has not configured any membership plans. Contact the administrator for more information.</p>
          </div>
        )}

        <div className="pricing-cards mp-cards">
          {plans.map((plan) => {
            const isCurrentPlan = membershipPlanKey === plan.key;
            const isLoadingPlan = pendingPlan === plan.key;
            const activePlanOrder = plans.map((p) => p.key);
            const currentPlanIndex = activePlanOrder.indexOf(membershipPlanKey);
            const planIndex = activePlanOrder.indexOf(plan.key);
            const isHigherTier = currentPlanIndex !== -1 && planIndex > currentPlanIndex;
            const shouldOpenPortal = canManageMembership && !isHigherTier;
            const isUpgradePlan = isActive && isHigherTier;
            const isFeatured = Boolean(plan.highlightLabel);
            const buttonLabel = (() => {
              if (canManageMembership) {
                if (isCurrentPlan) {
                  return t('membership.actions.manage');
                }
                if (isUpgradePlan) {
                  return t('membership.actions.upgrade', { planName: plan.name });
                }
                if (!isActive) {
                  return plan.cta;
                }
                return t('membership.actions.manage');
              }
              return plan.cta;
            })();

            const handleClick = () => {
              if (shouldOpenPortal) {
                handlePortal();
              } else if (isUpgradePlan) {
                handleUpgrade(plan.key, plan.name);
              } else {
                handleSubscribe(plan.key);
              }
            };

            const otherPlanPending = pendingPlan && pendingPlan !== plan.key;
            const isButtonDisabled = statusLoading
              || (shouldOpenPortal && portalPending)
              || (!shouldOpenPortal && (otherPlanPending || isLoadingPlan));

            return (
              <div
                key={plan.key}
                className={`price-card${isFeatured ? ' featured' : ''}${isCurrentPlan ? ' mp-current' : ''}`}
                data-badge={plan.highlightLabel || 'MOST POPULAR'}
              >
                {isCurrentPlan && isActive && (
                  <div className="mp-current-badge">{t('membership.planActiveBadge')}</div>
                )}
                <div className="price-tier-name">{plan.name}</div>
                {plan.tagline && <div className="mp-tagline">{plan.tagline}</div>}
                <div className="mp-priceline">
                  {plan.oldPrice && <span className="mp-oldprice">{plan.oldPrice}</span>}
                  <div className="price-tier-price">
                    {plan.priceAmount}
                    {plan.pricePeriod && <span className="period">{plan.pricePeriod}</span>}
                  </div>
                  {plan.discountLabel && <span className="mp-discount">{plan.discountLabel}</span>}
                </div>
                <ul className="price-features">
                  {plan.features.map((feature) => (
                    <li key={feature} style={{ whiteSpace: 'pre-line' }}>{feature}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={`btn ${isFeatured ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={handleClick}
                  disabled={isButtonDisabled}
                >
                  {isLoadingPlan ? <span className="spinner" /> : buttonLabel}
                </button>
              </div>
            );
          })}
        </div>

        {pageSettings.custom_html_below_plans && (
          <div className="mp-custom-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(pageSettings.custom_html_below_plans) }} />
        )}

        <div className="mp-extra-btns">
          {pageSettings.show_discord_button && pageSettings.discord_invite_url && (
            <a className="btn btn-discord" href={pageSettings.discord_invite_url} target="_blank" rel="noopener noreferrer">Join Discord</a>
          )}
          {pageSettings.show_telegram_button && pageSettings.telegram_url && (
            <a className="btn btn-ghost" href={pageSettings.telegram_url} target="_blank" rel="noopener noreferrer">Join Telegram</a>
          )}
        </div>
    </MarketingPage>
  );
};

export default Membership;
